import { Navigate, Outlet, lazyRouteComponent, redirect, createRootRoute, createRoute, createRouter } from "@tanstack/react-router"
import { TooltipProvider } from "@/components/ui/tooltip"
import { RedirectOnce } from "@/components/redirect"
import { useSession } from "@/lib/session"
import { AppPage, AppsIndexPage } from "@/routes/apps"
import { ChooseScopePage } from "@/routes/choose-scope"
import { AuthCallbackPage, ConnectPage } from "@/routes/connect"
import { DefinitionDetail } from "@/routes/definition-detail"
import { DefinitionsIndex, DefinitionsKindIndex, DefinitionsKindLayout } from "@/routes/definitions"
import { ExplorerLayout, LineagePage, TypeGraphPage } from "@/routes/explorer"
import { FindingsPage } from "@/routes/findings"
import { InboxPage } from "@/routes/inbox"
import { LedgerPage } from "@/routes/ledger"
import { ObjectDetailPage } from "@/routes/object-detail"
import { ObjectListPage, ObjectTypesPage } from "@/routes/objects"
import { OverviewPage } from "@/routes/overview"
import { ExplainPage, PeoplePage, PermissionsLayout, PersonPage, RulesPage } from "@/routes/permissions"
import { ScopeLayout } from "@/routes/scope-layout"
import { AreaPage, AreasPage } from "@/workspace/areas-page"
import { CollectionPage } from "@/workspace/collection-page"
import { HomePage } from "@/workspace/home-page"
import { DataHomePage } from "@/data/data-home"
import { ExplorePage } from "@/data/explore-page"
import { MetricsPage } from "@/data/metrics-page"
import { OntologyPage } from "@/data/ontology-page"
import { PolicyPage } from "@/data/policy-page"
import { SourcesPage } from "@/data/sources-page"
import { RecordPage } from "@/workspace/record-page"
import { WorkbookPage, WorkbooksPage } from "@/workbook/workbook-page"

const rootRoute = createRootRoute({
  component: () => (
    <TooltipProvider delayDuration={400}>
      <Outlet />
    </TooltipProvider>
  ),
  notFoundComponent: () => <Navigate to="/" />,
})

function Home() {
  const { connection } = useSession()
  return connection ? <ChooseScopePage /> : <RedirectOnce to="/connect" />
}

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: Home })
const connectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect",
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => (typeof search.returnTo === "string" ? { returnTo: search.returnTo } : {}),
  component: ConnectPage,
})
const authCallbackRoute = createRoute({ getParentRoute: () => rootRoute, path: "/auth/callback", component: AuthCallbackPage })
const scopeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/$orgId/$environmentId", component: ScopeLayout })
// Workspace: the business by meaning. Builder (below) keeps the control plane one switch away.
const homeScopeRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/", component: HomePage })
const areasRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/areas", component: AreasPage })
const areaRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/areas/$areaId", component: AreaPage })
const collectionRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/records/$objectType", component: CollectionPage })
const recordRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/records/$objectType/$key",
  validateSearch: (search: Record<string, unknown>): { via?: string } => (typeof search.via === "string" ? { via: search.via } : {}),
  component: RecordPage,
})
const overviewRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/builder", component: OverviewPage })
// Data: the analyst's view of the same cell (ontology, explore and pivot, metrics, sources, policy).
const dataHomeRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/data", component: DataHomePage })
const ontologyRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/data/ontology",
  validateSearch: (search: Record<string, unknown>): { type?: string } => (typeof search.type === "string" ? { type: search.type } : {}),
  component: OntologyPage,
})
const exploreRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/data/explore",
  validateSearch: (search: Record<string, unknown>): { type?: string } => (typeof search.type === "string" ? { type: search.type } : {}),
  component: ExplorePage,
})
const metricsRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/data/metrics", component: MetricsPage })
const sourcesRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/data/sources",
  validateSearch: (search: Record<string, unknown>): { type?: string; key?: string } => ({
    ...(typeof search.type === "string" ? { type: search.type } : {}),
    ...(typeof search.key === "string" ? { key: search.key } : {}),
  }),
  component: SourcesPage,
})
const policyRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/data/policy", component: PolicyPage })
const workbooksRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/data/workbooks", component: WorkbooksPage })
const workbookRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/data/workbooks/$workbookId",
  validateSearch: (search: Record<string, unknown>): { focus?: string } => (typeof search.focus === "string" ? { focus: search.focus } : {}),
  component: WorkbookPage,
})
const definitionsRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/definitions", component: DefinitionsIndex })
const definitionsKindRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/definitions/$kind", component: DefinitionsKindLayout })
const definitionsKindIndexRoute = createRoute({ getParentRoute: () => definitionsKindRoute, path: "/", component: DefinitionsKindIndex })
const definitionDetailRoute = createRoute({ getParentRoute: () => definitionsKindRoute, path: "/$artifactId", component: DefinitionDetail })
const explorerRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/explorer", component: ExplorerLayout })
const explorerIndexRoute = createRoute({
  getParentRoute: () => explorerRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/$orgId/$environmentId/explorer/types", params })
  },
})
const typesRoute = createRoute({ getParentRoute: () => explorerRoute, path: "/types", component: TypeGraphPage })
const lineageRoute = createRoute({ getParentRoute: () => explorerRoute, path: "/lineage", component: LineagePage })
const objectsRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/objects", component: ObjectTypesPage })
const objectListRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/objects/$objectType", component: ObjectListPage })
const objectDetailRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/objects/$objectType/$key", component: ObjectDetailPage })
const graphRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/graph",
  validateSearch: (search: Record<string, unknown>): { root?: string } => typeof search.root === "string" ? { root: search.root } : {},
  component: lazyRouteComponent(() => import("@/routes/graph-entry"), "GraphRoute"),
})
const ledgerRoute = createRoute({
  getParentRoute: () => scopeRoute, path: "/ledger", component: LedgerPage,
  validateSearch: (search: Record<string, unknown>): { after?: string; seq?: string; commit?: string; hash?: string } => {
    const position = (value: unknown) => typeof value === "string" && /^(0|[1-9]\d*)$/.test(value) && Number.isSafeInteger(Number(value)) ? value : undefined
    return { after: position(search.after), seq: position(search.seq), commit: position(search.commit), hash: typeof search.hash === "string" && search.hash.length <= 256 ? search.hash : undefined }
  },
})
// The JDM editor (and its antd island) loads only when a decision table is opened.
const decisionRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/decisions/$artifactId", component: lazyRouteComponent(() => import("@/routes/decision-editor"), "DecisionEditorPage") })
/** Query values as raw strings only (see parseSearch below). */
const rawSearch = (search: Record<string, unknown>): Record<string, string> => Object.fromEntries(Object.entries(search).flatMap(([k, v]) => (typeof v === "string" ? [[k, v]] : [])))
const appsRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/apps", component: AppsIndexPage })
// Context parameters arrive as query parameters, typed per the page's declared contextParams.
const appPageRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/apps/$appId/$pageId",
  // Raw query text only (see parseSearch below); each page decodes it against its declared types.
  validateSearch: (search: Record<string, unknown>): Record<string, string> => rawSearch(search),
  component: AppPage,
})
const findingsRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/findings", component: FindingsPage })
const permissionsRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/permissions", component: PermissionsLayout })
const permissionsIndexRoute = createRoute({
  getParentRoute: () => permissionsRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/$orgId/$environmentId/permissions/explain", params })
  },
})
const explainRoute = createRoute({
  getParentRoute: () => permissionsRoute,
  path: "/explain",
  validateSearch: (search: Record<string, unknown>): { principal?: string; action?: string; type?: string; key?: string } => rawSearch(search),
  component: ExplainPage,
})
const rulesRoute = createRoute({ getParentRoute: () => permissionsRoute, path: "/rules", validateSearch: (search: Record<string, unknown>): { rule?: string } => rawSearch(search), component: RulesPage })
const peopleRoute = createRoute({ getParentRoute: () => permissionsRoute, path: "/people", component: PeoplePage })
const personRoute = createRoute({ getParentRoute: () => permissionsRoute, path: "/people/$personId", component: PersonPage })
const inboxRoute = createRoute({ getParentRoute: () => scopeRoute, path: "/inbox", component: InboxPage })

export const routeTree = rootRoute.addChildren([
  homeRoute,
  connectRoute,
  authCallbackRoute,
  scopeRoute.addChildren([
    homeScopeRoute,
    areasRoute,
    areaRoute,
    collectionRoute,
    recordRoute,
    overviewRoute,
    dataHomeRoute,
    ontologyRoute,
    exploreRoute,
    metricsRoute,
    sourcesRoute,
    policyRoute,
    workbooksRoute,
    workbookRoute,
    definitionsRoute,
    definitionsKindRoute.addChildren([definitionsKindIndexRoute, definitionDetailRoute]),
    explorerRoute.addChildren([explorerIndexRoute, typesRoute, lineageRoute]),
    objectsRoute,
    objectListRoute,
    objectDetailRoute,
    graphRoute,
    ledgerRoute,
    inboxRoute,
    appsRoute,
    appPageRoute,
    findingsRoute,
    permissionsRoute.addChildren([permissionsIndexRoute, explainRoute, rulesRoute, peopleRoute, personRoute]),
    decisionRoute,
  ]),
])

// Query values stay raw text: the default JSON search parsing would turn `9007199254740993` into a
// rounded number before a page could decode it against its declared parameter type.
export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  parseSearch: (search) => Object.fromEntries(new URLSearchParams(search)),
  stringifySearch: (search) => {
    const params = new URLSearchParams(Object.entries(search).flatMap(([k, v]) => (typeof v === "string" ? [[k, v]] : [])))
    const text = params.toString()
    return text ? `?${text}` : ""
  },
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}
